import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loading } from '@bumble/app/components/Loading';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';

interface PduCreateProps {
  handleClose: () => void;
  nodeId: string;
}

export const PduCreate = ({ handleClose, nodeId }: PduCreateProps) => {
  const api = useApi();
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [pduName, setPduName] = useState<string>('');
  const [vendorName, setVendorName] = useState<string>('');
  const [ipAddr, setIpAddr] = useState<string>('');

  const { isLoading, data: vendorNames } = useQuery(
    ['pdu-vendor-names'],
    api.getPduVendorNamesApiV1GetPduVendorNamesGet.bind(null, {}),
    { enabled: true }
  );

  const createPdu = useMutation(api.createPduApiV1CreatePduPost, {
    onSuccess() {
      setLoadingModalOpen(false);
      toast.success('PDU created successfully.');
      handleClose();
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      apiError(error);
    },
  });

  const isFormValid = useMemo(() => {
    return pduName.trim() !== '' && vendorName !== '' && ipAddr.trim() !== '';
  }, [pduName, vendorName, ipAddr]);

  const handleSubmit = () => {
    if (!pduName.trim()) {
      toast.error('PDU Name is required');
      return;
    }
    if (!vendorName) {
      toast.error('Vendor Name is required');
      return;
    }
    if (!ipAddr.trim()) {
      toast.error('IP Address is required');
      return;
    }

    setLoadingModalOpen(true);
    createPdu.mutate({
      node_id: nodeId,
      pdu_name: pduName.trim(),
      vendor_name: vendorName,
      ip_addr: ipAddr.trim(),
    });
  };

  if (isLoading) return <Loading />;

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="pdu_name"
            label="PDU Name"
            variant="outlined"
            value={pduName}
            onChange={(e) => setPduName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="vendor_name_label">Vendor Name</InputLabel>
            <Select
              labelId="vendor_name_label"
              id="vendor_name"
              label="Vendor Name"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
            >
              {vendorNames?.data?.map((vendor: string) => (
                <MenuItem value={vendor} key={vendor}>
                  {vendor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="ip_addr"
            label="IP Address"
            variant="outlined"
            value={ipAddr}
            onChange={(e) => setIpAddr(e.target.value)}
            placeholder="192.168.1.100"
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!isFormValid}>
              Create
            </Button>
          </Box>
        </Grid>
      </Grid>
      {loadingModalOpen && <PageLoading content="Creating PDU..." />}
    </Box>
  );
};
